import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { PropertyFilters, SavedSearch } from '@/types/property';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';

export function useSavedSearches() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['saved-searches', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('saved_searches')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data.map(search => ({
        ...search,
        filters: search.filters as PropertyFilters
      })) as SavedSearch[];
    },
    enabled: !!user,
  });
}

export function useSaveSearch() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, filters, emailAlerts = false }: { 
      name: string; 
      filters: PropertyFilters; 
      emailAlerts?: boolean 
    }) => {
      if (!user) throw new Error('Must be logged in');

      const { error } = await supabase
        .from('saved_searches')
        .insert({ 
          user_id: user.id, 
          name, 
          filters: filters as unknown as Json,
          email_alerts: emailAlerts 
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
      toast.success('Search saved successfully');
    },
    onError: () => {
      toast.error('Failed to save search');
    },
  });
}

export function useDeleteSavedSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (searchId: string) => {
      const { error } = await supabase
        .from('saved_searches')
        .delete()
        .eq('id', searchId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
      toast.success('Search deleted');
    },
    onError: () => {
      toast.error('Failed to delete search');
    },
  });
}